import {
  PACKAGE_ROUTES,
  PRODUCT_ROUTES,
  packageListResponseSchema,
  packageResponseSchema,
  packageUpdateSchema,
  productListResponseSchema,
  type Package,
  type PackageCategory,
  type PackageInput,
  type PackageUpdate,
  type ProductList,
  type ProductStatus,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Los paquetes vistos desde el panel.
 *
 * Todas las llamadas pasan por `authFetch`: adjunta el access token,
 * renueva una vez si expiró y cierra la sesión si el token es inválido.
 * Ninguna pantalla de este módulo toca `fetch` a mano.
 *
 * `baseUrl: ""` porque `PACKAGE_ROUTES` YA trae el prefijo `/api`. Sin
 * esto quedaría `/api/api/admin/packages` y el 404 se descubriría en
 * ejecución, no al compilar.
 * ═══════════════════════════════════════════════════════════════════
 */
const NO_PREFIX = { baseUrl: "" } as const;

export interface AdminPackagesQuery {
  category?: PackageCategory;
  status?: ProductStatus;
  page: number;
  perPage: number;
}

/**
 * La URL del listado con sus filtros.
 *
 * El orden de los parámetros es FIJO y no depende del orden en que se
 * tocaron los controles: así dos estados idénticos producen la misma
 * URL, react-query los reconoce como la misma entrada de caché y las
 * pruebas pueden responder por ruta exacta.
 *
 * NO viaja `sort`. `packageListQuerySchema` declara cuatro parámetros
 * —categoría, estado, página y tamaño— y ninguno es el orden; mandar uno
 * inventado sería una promesa que el servidor no firmó. El orden se
 * aplica en el cliente y está documentado donde se aplica.
 */
export function adminPackagesUrl(query: AdminPackagesQuery): string {
  const params = new URLSearchParams();
  if (query.category) params.set("category", query.category);
  if (query.status) params.set("status", query.status);
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  return `${PACKAGE_ROUTES.adminList}?${params.toString()}`;
}

export interface AdminPackageList {
  items: Package[];
  total: number;
  page: number;
  perPage: number;
}

export function listAdminPackages(query: AdminPackagesQuery): Promise<AdminPackageList> {
  return authFetch(adminPackagesUrl(query), packageListResponseSchema, NO_PREFIX);
}

export async function getAdminPackage(id: string): Promise<Package> {
  const data = await authFetch(
    PACKAGE_ROUTES.adminOne(id),
    packageResponseSchema,
    NO_PREFIX,
  );
  return data.package;
}

export async function createPackage(input: PackageInput): Promise<Package> {
  const data = await authFetch(PACKAGE_ROUTES.adminCreate, packageResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
  return data.package;
}

/**
 * Editar.
 *
 * El `slug` NO viaja: `packageUpdateSchema` lo omite por contrato, y
 * aquí se vuelve a pasar por el esquema antes de enviar para que un
 * campo de más —o mal formado— se detecte aquí y no en el servidor.
 *
 * Después de validar se envían SOLO las claves que venían en `patch`.
 * `packageUpdateSchema` es un `.partial()` del de entrada, y los campos
 * con `.default()` del contrato (`status`, `sortOrder`) siguen aplicando
 * su valor por omisión aunque la clave no esté: sin este filtro, un
 * PATCH para corregir una falta de ortografía mandaría también
 * `status: "draft"` y DESPUBLICARÍA el paquete.
 *
 * Y el PRECIO no viaja nunca, ni aquí ni al crear: `packageInputSchema`
 * no tiene `prices` ni `listPrices`. Lo que se manda es el descuento; el
 * precio lo calcula el servidor sumando los productos. Ver el bloque
 * calculado del formulario.
 */
export async function updatePackage(id: string, patch: PackageUpdate): Promise<Package> {
  const validated = packageUpdateSchema.parse(patch) as Record<string, unknown>;
  const body = Object.fromEntries(
    Object.keys(patch).map((key) => [key, validated[key]]),
  );
  const data = await authFetch(PACKAGE_ROUTES.adminOne(id), packageResponseSchema, {
    ...NO_PREFIX,
    method: "PATCH",
    body,
  });
  return data.package;
}

/**
 * Archivar.
 *
 * NO existe un borrado de verdad y no se pide uno: un paquete borrado
 * deja cada SUSCRIPCIÓN viva apuntando a la nada, y una suscripción es
 * un cobro mensual en marcha. Archivar lo saca del catálogo público y lo
 * deja intacto para quien ya lo tiene contratado.
 *
 * ── Por qué es un PATCH y no un DELETE ───────────────────────────────
 * Porque el contrato no declara un borrado. `package.ts` expone
 * `packageResponseSchema` para `adminOne` y ningún esquema de
 * "eliminado"; el catálogo de productos sí lo tiene
 * (`productDeletedResponseSchema`) y por eso allí el verbo es DELETE.
 * Inventar aquí un DELETE sería acordar con el backend por un canal que
 * no es el contrato, y descubrir el desacuerdo en producción. Archivar
 * es exactamente "pon el estado en `archived`", que es lo que
 * `packageUpdateSchema` acepta.
 */
export function archivePackage(id: string): Promise<Package> {
  return updatePackage(id, { status: "archived" });
}

// ── El catálogo, para el selector de productos ──────────────────────

/**
 * Los productos que se pueden meter en un paquete.
 *
 * Viene del catálogo del panel (`PRODUCT_ROUTES.adminList`) y no del
 * público: un paquete se arma antes de publicar sus productos, así que
 * el selector tiene que enseñar también los borradores. Es el mismo
 * endpoint que usa la pantalla de productos; se pide aparte y con su
 * propia llave de caché para que este módulo no dependa del de al lado.
 *
 * Se piden 100 —el techo que admite `productListQuerySchema`— en una
 * sola página. El selector es un desplegable: paginarlo sería pedirle a
 * quien arma un paquete que adivine en qué página está el producto que
 * busca. Si el catálogo pasa de cien, `total` lo dice y la pantalla
 * avisa en vez de enseñar una lista incompleta en silencio.
 */
export const PRODUCT_PICKER_PER_PAGE = 100;

export function productPickerUrl(): string {
  const params = new URLSearchParams();
  params.set("page", "1");
  params.set("perPage", String(PRODUCT_PICKER_PER_PAGE));
  params.set("sort", "recommended");
  return `${PRODUCT_ROUTES.adminList}?${params.toString()}`;
}

export function listProductsForPicker(): Promise<ProductList> {
  return authFetch(productPickerUrl(), productListResponseSchema, NO_PREFIX);
}
